/**
 * Community slideshow (<community-slideshow>), sections/community-slideshow.liquid
 *
 * Loads one public collection as a guest (wfGallery.api.galleries.get, the
 * same call the collection page makes) and renders its pictures as one row:
 * fixed height, each picture as wide as its own format, a caption under it.
 *
 * Caption lines, per picture N (1-based, collection order):
 *   film   = block override for N, else image.film from the backend
 *            (except HIDDEN_FILMS such as "Anderer Filmtyp")
 *   credit = block override for N, else image.credit from the backend
 * Empty lines are not rendered; a picture with neither has no caption.
 *
 * Arrows (desktop) jump to the start of the next/previous picture and hide
 * themselves from the scroll position, so they stay right when the row is
 * scrolled by trackpad or touch. The progress strokes mark the picture at
 * the left edge and work as a scrubber (bindScrub): press to jump, drag to
 * scroll, and with a mouse the stroke under it is magnified.
 *
 * A collection that cannot be read hides the section for visitors; in the
 * theme editor it stays with a hint, so the mistake is noticed.
 */

if (!customElements.get('community-slideshow')) {
  // Backend film names that say nothing to a visitor; shown as no film line.
  const HIDDEN_FILMS = ['Anderer Filmtyp'];
  const SIZE_WAIT_MS = 1500;
  // Film names match block names regardless of case and spacing.
  const filmKey = (name) => String(name || '').trim().replace(/\s+/g, ' ').toLowerCase();

  class CommunitySlideshow extends HTMLElement {
    connectedCallback() {
      if (this.started) return;
      this.started = true;
      this.track = this.querySelector('[data-slideshow-track]');
      this.prevButton = this.querySelector('[data-slideshow-prev]');
      this.nextButton = this.querySelector('[data-slideshow-next]');
      this.progress = this.querySelector('[data-slideshow-progress]');
      this.overrides = this.readOverrides();
      this.cards = this.readCards();

      this.prevButton.addEventListener('click', () => this.go(-1));
      this.nextButton.addEventListener('click', () => this.go(1));
      this.track.addEventListener('scroll', () => this.update(), { passive: true });
      window.addEventListener('resize', () => this.update());
      this.bindDrag();
      this.bindScrub();
      this.bindFilmCards();

      this.load();
    }

    /** Film card templates keyed by normalised film name (see filmKey). */
    readCards() {
      const cards = new Map();
      this.querySelectorAll('template[data-film-card]').forEach((template) => {
        cards.set(filmKey(template.dataset.filmCard), template);
      });
      return cards;
    }

    /**
     * The film card: hover a film name with the mouse, or tap it, and a small
     * window with the film's product opens above it (templates rendered by
     * the section from its "Film zum Produkt" blocks). With a mouse it stays
     * while the pointer is on the name or the window and closes shortly
     * after it leaves; a click pins it. A tap toggles it. Any press outside,
     * Escape, or scrolling the row closes it.
     */
    bindFilmCards() {
      // Not `this.popover`: HTMLElement already has that (the Popover API),
      // and assigning it sets a `popover` attribute on the slideshow.
      this.filmPopover = this.querySelector('[data-film-popover]');
      if (!this.filmPopover || !this.cards.size) return;
      let current = null;
      let pinned = false;
      let closeTimer = null;
      let lastPointer = 'mouse';

      const cancelClose = () => clearTimeout(closeTimer);
      const close = () => {
        cancelClose();
        if (!current) return;
        current.setAttribute('aria-expanded', 'false');
        current = null;
        pinned = false;
        this.filmPopover.classList.remove('is-open');
        setTimeout(() => {
          if (!current) this.filmPopover.hidden = true;
        }, 150);
      };
      const open = (button) => {
        cancelClose();
        if (current === button) return;
        if (current) current.setAttribute('aria-expanded', 'false');
        const template = this.cards.get(filmKey(button.textContent));
        if (!template) return;
        current = button;
        button.setAttribute('aria-expanded', 'true');
        this.filmPopover.setAttribute('aria-label', button.textContent);
        this.filmPopover.replaceChildren(template.content.cloneNode(true));
        this.filmPopover.hidden = false;
        // Square picture as tall as the name plus the button beside it.
        const thumb = this.filmPopover.querySelector('.film-card__thumb');
        const body = this.filmPopover.querySelector('.film-card__body');
        if (thumb && body) thumb.style.width = `${body.offsetHeight}px`;
        this.placePopover(button);
        requestAnimationFrame(() => this.filmPopover.classList.add('is-open'));
      };
      const closeSoon = () => {
        if (pinned) return;
        cancelClose();
        closeTimer = setTimeout(close, 200);
      };

      this.addEventListener('pointerdown', (event) => {
        lastPointer = event.pointerType;
      });
      this.track.addEventListener('pointerover', (event) => {
        const button = event.target.closest('[data-film-button]');
        if (button && event.pointerType === 'mouse' && !this.track.classList.contains('is-dragging')) open(button);
      });
      this.track.addEventListener('pointerout', (event) => {
        if (event.pointerType === 'mouse' && event.target.closest('[data-film-button]')) closeSoon();
      });
      this.filmPopover.addEventListener('pointerenter', cancelClose);
      this.filmPopover.addEventListener('pointerleave', (event) => {
        if (event.pointerType === 'mouse') closeSoon();
      });
      this.track.addEventListener('click', (event) => {
        const button = event.target.closest('[data-film-button]');
        if (!button) return;
        if (this.justDragged) {
          this.justDragged = false;
          return;
        }
        if (current === button && (pinned || lastPointer !== 'mouse')) {
          close();
          return;
        }
        open(button);
        pinned = true;
      });
      document.addEventListener('pointerdown', (event) => {
        if (current && !this.filmPopover.contains(event.target) && !event.target.closest('[data-film-button]')) close();
      });
      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && current) {
          const button = current;
          close();
          button.focus();
        }
      });
      this.track.addEventListener('scroll', close, { passive: true });
      window.addEventListener('resize', close);
    }

    /** Above the film name, left-aligned to it, kept inside the viewport. */
    placePopover(button) {
      const box = this.getBoundingClientRect();
      const name = button.getBoundingClientRect();
      const width = this.filmPopover.offsetWidth;
      const left = Math.min(Math.max(name.left - box.left, 0), Math.max(box.width - width, 0));
      const top = name.top - box.top - this.filmPopover.offsetHeight - 8;
      this.filmPopover.style.left = `${left}px`;
      this.filmPopover.style.top = `${top}px`;
    }

    /**
     * Mouse drag on desktop; touch keeps the native swipe. Snapping is off
     * while dragging (it would fight the pointer) and the row settles on the
     * nearest picture on release.
     */
    bindDrag() {
      let drag = null;
      this.track.addEventListener('pointerdown', (event) => {
        if (event.pointerType !== 'mouse' || event.button !== 0) return;
        drag = { x: event.clientX, left: this.track.scrollLeft, moved: false };
      });
      this.track.addEventListener('pointermove', (event) => {
        if (!drag) return;
        const dx = event.clientX - drag.x;
        if (!drag.moved) {
          if (Math.abs(dx) < 4) return;
          drag.moved = true;
          this.track.setPointerCapture(event.pointerId);
          this.track.classList.add('is-dragging');
        }
        this.track.scrollLeft = drag.left - dx;
      });
      const end = () => {
        if (!drag) return;
        const { moved } = drag;
        drag = null;
        if (!moved) return;
        // The click that ends a drag must not open a film card.
        this.justDragged = true;
        setTimeout(() => {
          this.justDragged = false;
        }, 0);
        this.track.classList.remove('is-dragging');
        this.settle();
      };
      this.track.addEventListener('pointerup', end);
      this.track.addEventListener('pointercancel', end);
    }

    /**
     * The progress strokes as a scrubber. The active stroke (the picture at
     * the left edge) stays thin and brown. The stroke under the pointer is
     * magnified (data-hover, wider at its neighbours' expense), with a mouse
     * only: on touch the bar just jumps and scrubs. A
     * click or tap scrolls to that stroke's picture; dragging scrolls the row
     * along with the pointer and settles on the nearest picture on release.
     *
     * The stroke under the pointer comes from the pointer's position along
     * the bar, not from the strokes' boxes: those change width while they
     * magnify, which would make the choice flicker at a boundary.
     */
    bindScrub() {
      let scrub = null;
      // Position of `x` along the bar, 0 to 1.
      const fraction = (x) => {
        const rect = this.progress.getBoundingClientRect();
        return rect.width ? Math.min(Math.max((x - rect.left) / rect.width, 0), 0.9999) : 0;
      };
      const indexAt = (x) => Math.floor(fraction(x) * this.strokes().length);
      // Shares of the bar (flex-grow, 1 each at rest): the magnified stroke
      // takes exactly what its neighbours give up, so every other stroke
      // keeps its width. At either end there is one neighbour, which gives
      // up less so it does not vanish.
      const magnify = (x) => {
        const strokes = this.strokes();
        const index = x == null ? -1 : indexAt(x);
        const atEnd = index === 0 || index === strokes.length - 1;
        strokes.forEach((stroke, i) => {
          const hover = i === index;
          const near = index >= 0 && Math.abs(i - index) === 1;
          stroke.toggleAttribute('data-hover', hover);
          stroke.style.flexGrow = hover ? (atEnd ? '1.6' : '2') : near ? (atEnd ? '0.4' : '0.5') : '';
        });
      };

      this.progress.addEventListener('pointermove', (event) => {
        if (!this.strokes().length) return;
        if (!scrub) {
          if (event.pointerType === 'mouse') magnify(event.clientX);
          return;
        }
        if (scrub.mouse) magnify(event.clientX);
        if (!scrub.moved) {
          if (Math.abs(event.clientX - scrub.x) < 4) return;
          scrub.moved = true;
          this.track.classList.add('is-dragging');
        }
        this.track.scrollLeft = fraction(event.clientX) * (this.track.scrollWidth - this.track.clientWidth);
      });
      this.progress.addEventListener('pointerleave', (event) => {
        if (!scrub && event.pointerType === 'mouse') magnify(null);
      });
      this.progress.addEventListener('pointerdown', (event) => {
        if (!this.strokes().length || (event.pointerType === 'mouse' && event.button !== 0)) return;
        event.preventDefault();
        this.progress.setPointerCapture(event.pointerId);
        scrub = { x: event.clientX, moved: false, mouse: event.pointerType === 'mouse' };
        if (scrub.mouse) magnify(event.clientX);
      });
      const end = (event, cancelled = false) => {
        if (!scrub) return;
        const { moved } = scrub;
        scrub = null;
        if (moved) {
          this.track.classList.remove('is-dragging');
          if (!cancelled) this.settle();
          return;
        }
        if (!cancelled) this.goTo(indexAt(event.clientX));
      };
      this.progress.addEventListener('pointerup', (event) => end(event));
      this.progress.addEventListener('pointercancel', (event) => end(event, true));
    }

    strokes() {
      return Array.from(this.progress.querySelectorAll('[data-stroke]'));
    }

    /** Scrolls to the picture whose start is nearest the left edge. */
    settle() {
      const left = this.track.scrollLeft;
      const maxScroll = this.track.scrollWidth - this.track.clientWidth;
      if (left >= maxScroll - 1) return;
      const nearest = this.items().reduce((best, item) =>
        Math.abs(item.offsetLeft - left) < Math.abs(best.offsetLeft - left) ? item : best);
      this.track.scrollTo({ left: Math.min(nearest.offsetLeft, maxScroll), behavior: 'smooth' });
    }

    /** Block overrides keyed by picture number; the last block for a number wins. */
    readOverrides() {
      const byPosition = new Map();
      try {
        const list = JSON.parse(this.querySelector('[data-slideshow-overrides]')?.textContent || '[]');
        for (const { position, film, credit } of list) {
          const n = Number(position);
          if (Number.isInteger(n) && n > 0) byPosition.set(n, { film, credit });
        }
      } catch (error) {
        // Malformed JSON cannot come from Liquid's `json`; ignore rather than break the row.
      }
      return byPosition;
    }

    async load() {
      const { api, ui } = window.wfGallery;
      try {
        const { images } = await api.galleries.get(this.dataset.collectionId);
        if (!images.length) throw new Error('empty collection');
        await this.measure(images, ui);
        this.render(images);
      } catch (error) {
        this.fail();
      }
    }

    /**
     * The backend sends no size for most pictures, and a frame that only
     * learns its shape when its picture loads changes width under the
     * visitor's finger, shifting everything after it. So every picture's
     * shape is read from its file header (a small range request) before the
     * row is drawn. Capped at SIZE_WAIT_MS; a picture still unknown then
     * keeps the 3:2 placeholder and adjusts on load.
     */
    async measure(images, ui) {
      const unknown = images.filter((image) => !(image.width && image.height));
      const sized = Promise.all(
        unknown.map(async (image) => {
          const size = await ui.uprightSize(image).catch(() => null);
          if (size) Object.assign(image, size);
        }),
      );
      await Promise.race([sized, new Promise((resolve) => setTimeout(resolve, SIZE_WAIT_MS))]);
    }

    fail() {
      if (window.Shopify?.designMode) {
        const hint = this.querySelector('[data-slideshow-hint]');
        hint.textContent = this.dataset.unavailable;
        hint.hidden = false;
        this.track.hidden = true;
      } else {
        (this.closest('.shopify-section') || this).hidden = true;
      }
    }

    render(images) {
      const showFilm = this.dataset.showFilm === 'true';
      const showCredit = this.dataset.showCredit === 'true';

      const items = images.map((image, index) => {
        const override = this.overrides.get(index + 1) || {};
        const backendFilm = HIDDEN_FILMS.includes(image.film) ? '' : image.film;
        const film = showFilm ? override.film || backendFilm || '' : '';
        const credit = showCredit ? override.credit || image.credit || '' : '';
        return this.item(image, film, credit);
      });
      this.track.replaceChildren(...items);

      this.progress.replaceChildren(
        ...images.map(() => {
          const stroke = document.createElement('span');
          stroke.dataset.stroke = '';
          return stroke;
        }),
      );
      this.progress.style.setProperty('--strokes', String(images.length));
      this.progress.hidden = images.length < 2;
      this.update();
    }

    /**
     * One picture. Shown from the ORIGINAL file, not the backend preview:
     * many previews are 600px (soft at this height) and some lie on their
     * side, and turning them upright needs CSS that not every phone browser
     * lays out the same. The browser turns an original upright from its EXIF
     * by itself. With an image resizer configured, its sizes are used instead.
     *
     * No pulsing skeleton: the frame is a calm surface and the picture fades
     * in once decoded (.is-loaded).
     */
    item(image, film, credit) {
      const li = document.createElement('li');
      li.className = 'community-slideshow__item';

      const frame = document.createElement('div');
      frame.className = 'community-slideshow__frame';
      // Width = height x ratio, as a plain number (community-slideshow.liquid).
      // The backend's or the file header's size (measure), else 3:2 until the
      // picture itself has loaded.
      const sized = Boolean(image.width && image.height);
      if (sized) frame.style.setProperty('--slide-ratio', String(image.width / image.height));

      const img = document.createElement('img');
      img.alt = film || '';
      img.draggable = false;
      img.decoding = 'async';
      img.loading = 'lazy';
      const srcset = image.srcset('large');
      if (srcset) {
        img.srcset = srcset;
        img.sizes = '(min-width: 1024px) 600px, 320px';
      }
      img.src = image.src('large');

      const shown = () => {
        if (!sized && img.naturalWidth && img.naturalHeight) {
          frame.style.setProperty('--slide-ratio', String(img.naturalWidth / img.naturalHeight));
          this.update();
        }
        frame.classList.add('is-loaded');
      };
      if (img.complete && img.naturalWidth) shown();
      else {
        img.addEventListener('load', shown, { once: true });
        img.addEventListener('error', () => frame.classList.add('is-loaded'), { once: true });
      }

      frame.append(img);
      li.append(frame);

      if (film || credit) {
        const caption = document.createElement('p');
        caption.className = 'community-slideshow__caption';
        if (film) {
          // A film with a "Film zum Produkt" block is a button for its card.
          const hasCard = this.cards.has(filmKey(film));
          const line = document.createElement(hasCard ? 'button' : 'span');
          line.className = `community-slideshow__film body${hasCard ? ' community-slideshow__film-button' : ''}`;
          line.textContent = film;
          if (hasCard) {
            line.type = 'button';
            line.dataset.filmButton = '';
            line.setAttribute('aria-haspopup', 'dialog');
            line.setAttribute('aria-expanded', 'false');
          }
          caption.append(line);
        }
        if (credit) {
          const line = document.createElement('span');
          line.className = 'community-slideshow__credit label';
          line.textContent = credit;
          caption.append(line);
        }
        li.append(caption);
      }
      return li;
    }

    items() {
      return Array.from(this.track.children);
    }

    go(direction) {
      const left = this.track.scrollLeft;
      const items = this.items();
      const target = direction > 0
        ? items.find((item) => item.offsetLeft > left + 1)
        : items.filter((item) => item.offsetLeft < left - 1).pop();
      if (target) this.track.scrollTo({ left: target.offsetLeft, behavior: 'smooth' });
    }

    /** Scrolls picture `index` to the left edge, or as far as the row goes. */
    goTo(index) {
      const item = this.items()[index];
      if (!item) return;
      const maxScroll = this.track.scrollWidth - this.track.clientWidth;
      this.track.scrollTo({ left: Math.min(item.offsetLeft, maxScroll), behavior: 'smooth' });
    }

    update() {
      const left = this.track.scrollLeft;
      const maxScroll = this.track.scrollWidth - this.track.clientWidth;
      this.prevButton.hidden = left <= 1;
      this.nextButton.hidden = left >= maxScroll - 1;

      const items = this.items();
      let active = 0;
      items.forEach((item, index) => {
        if (item.offsetLeft <= left + 1) active = index;
      });
      if (left >= maxScroll - 1) active = items.length - 1;
      this.strokes().forEach((stroke, index) => {
        stroke.toggleAttribute('data-active', index === active);
      });
    }
  }

  customElements.define('community-slideshow', CommunitySlideshow);
}
